import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../redux/store";
import { getPaymentStatusAny as getPaymentInfo } from "../../../helpers/getPaymentStatusAny";
import styles from "./PaymentConfirm.module.scss";

export const PaymentConfirm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const isInProfile = location.pathname.startsWith('/profile/payment/confirm');
  const isInPayment = location.pathname.startsWith('/payment/confirm');
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("Обработка платежа...");
  const [currentStep, setCurrentStep] = useState<string>("Проверка платежа...");
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);

  useEffect(() => {
    if (!polling || !paymentId) return;

    const MAX_ATTEMPTS = 150;
    const POLLING_INTERVAL = 2000;

    if (pollingAttempts >= MAX_ATTEMPTS) {
      setStatus("error");
      setMessage("Превышено время ожидания. Проверьте статус платежа в личном кабинете.");
      setPolling(false);
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        setCurrentStep(`Ожидание подтверждения платежа... (${pollingAttempts + 1}/${MAX_ATTEMPTS})`);
        
        const paymentInfo = await getPaymentInfo(paymentId);
        const paymentStatus = paymentInfo?.status;

        if (paymentStatus === "succeeded") {
          setPolling(false);
          await processSuccessfulPayment(paymentId, paymentInfo);
        } else if (paymentStatus === "canceled" || paymentStatus === "cancelled") {
          setPolling(false);
          setStatus("error");
          setMessage("Платеж отменен");
          setTimeout(() => {
            if (window.opener) {
              window.close();
            } else {
              if (isInPayment) {
                navigate("/payment");
              } else {
                navigate("/profile/cards");
              }
            }
          }, 3000);
        } else {
          setPollingAttempts(prev => prev + 1);
        }
      } catch (error: any) {
        setPollingAttempts(prev => prev + 1);
      }
    };

    const intervalId = setTimeout(checkPaymentStatus, POLLING_INTERVAL);

    return () => {
      clearTimeout(intervalId);
    };
  }, [polling, paymentId, pollingAttempts, navigate, dispatch]);

  const processSuccessfulPayment = useCallback(async (paymentId: string, paymentInfo: any) => {
    try {
      // Раньше здесь была ветка «проверка карты»: списать 1–11 ₽, вернуть их
      // и сохранить токен карты. Это был механизм привязки карт ЮKassa, а её
      // с сайта убрали. У Т-Кассы привязка другая (CustomerKey + Recurrent +
      // Rebill) и пока не сделана, поэтому остался один путь — обычная оплата.
        const savedGuid = localStorage.getItem("lastPaymentGuid") || (window as any).__lastPaymentGuid || '';
        if (savedGuid) {
          localStorage.removeItem("lastPaymentGuid");
          delete (window as any).__lastPaymentGuid;
        }
        
        if (window.opener && !window.opener.closed) {
          try {
            const guidParam = savedGuid ? `&guid=${savedGuid}` : '';
            window.opener.location.href = `/payment-success?payment_id=${paymentId}${guidParam}`;
            setTimeout(() => {
              window.close();
            }, 500);
          } catch (e) {
            window.close();
          }
        } else {
          navigate("/payment-success", {
            state: { paymentId: paymentId, guid: savedGuid },
          });
        }
    } catch (error: any) {
      setStatus("error");
      setMessage("Ошибка обработки платежа");
      setTimeout(() => {
        if (isInPayment) {
          navigate("/payment");
        } else {
          navigate("/profile/cards");
        }
      }, 3000);
    }
  }, [dispatch, navigate, isInPayment]);

  useEffect(() => {
    const processPayment = async () => {
      try {
        setStatus("loading");
        setMessage("Обработка платежа...");
        setCurrentStep("Проверка платежа...");
        
        
        let paymentId = searchParams.get("payment_id") || 
                       searchParams.get("paymentId") ||
                       searchParams.get("payment-id") ||
                       searchParams.get("orderId");
        
        if (!paymentId) {
          const hash = window.location.hash;
          if (hash) {
            const hashParams = new URLSearchParams(hash.substring(1));
            paymentId = hashParams.get("payment_id") || 
                       hashParams.get("paymentId") || 
                       hashParams.get("orderId");
          }
        }

        if (!paymentId) {
          const lastCardPaymentId = localStorage.getItem("lastCardPaymentId");
          const lastPaymentId = localStorage.getItem("lastPaymentId");
          
          if (lastPaymentId) {
            paymentId = lastPaymentId;
            localStorage.removeItem("lastPaymentId");
            localStorage.removeItem("lastPaymentGuid");
          } else if (lastCardPaymentId) {
            paymentId = lastCardPaymentId;
            localStorage.removeItem("lastCardPaymentId");
          }
        }

        if (!paymentId) {
          setStatus("error");
          setMessage("Не найден ID платежа. Проверьте историю платежей.");
          setTimeout(() => {
            if (window.opener) {
              window.close();
            } else {
              if (isInPayment) {
                navigate("/payment");
              } else {
                navigate("/profile/cards");
              }
            }
          }, 3000);
          return;
        }

        setPaymentId(paymentId);
        
        const savedGuid = localStorage.getItem("lastPaymentGuid");
        if (savedGuid) {
          (window as any).__lastPaymentGuid = savedGuid;
        }

        setCurrentStep("Получение информации о платеже...");
        const paymentInfo = await getPaymentInfo(paymentId);

        if (!paymentInfo) {
          setStatus("error");
          setMessage("Не удалось получить информацию о платеже");
          setTimeout(() => {
            if (window.opener) {
              window.close();
            } else {
              if (isInPayment) {
                navigate("/payment");
              } else {
                navigate("/profile/cards");
              }
            }
          }, 3000);
          return;
        }

        const paymentStatus = paymentInfo.status;

        if (paymentStatus === "succeeded") {
          await processSuccessfulPayment(paymentId, paymentInfo);
        } else if (paymentStatus === "canceled" || paymentStatus === "cancelled") {
          setStatus("error");
          setMessage("Платеж отменен");
          setTimeout(() => {
            if (window.opener) {
              window.close();
            } else {
              if (isInPayment) {
                navigate("/payment");
              } else {
                navigate("/profile/cards");
              }
            }
          }, 3000);
        } else {
          setCurrentStep(`Ожидание подтверждения платежа... (статус: ${paymentStatus})`);
          setPolling(true);
          setPollingAttempts(0);
        }
      } catch (error: any) {
        setStatus("error");
        setMessage("Ошибка обработки платежа");
        setTimeout(() => {
          if (window.opener) {
            window.close();
          } else {
            if (isInPayment) {
              navigate("/payment");
            } else {
              navigate("/profile/cards");
            }
          }
        }, 3000);
      }
    };

    processPayment();
  }, [searchParams, navigate, dispatch, isInPayment]);

  return (
    <div className={`${styles.container} ${isInProfile || isInPayment ? styles.containerInProfile : ''}`}>
      <div className={styles.content}>
        {status === "loading" && (
          <>
            <div className={styles.spinner}></div>
            <p className={styles.message}>{currentStep || message}</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className={styles.successIcon}>✓</div>
            <p className={styles.message}>{message}</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className={styles.errorIcon}>✗</div>
            <p className={styles.message}>{message}</p>
          </>
        )}
      </div>
    </div>
  );
};

